import { useState } from 'react'
import Box from '@mui/joy/Box'
import Stack from '@mui/joy/Stack'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Tabs from '@mui/joy/Tabs'
import TabList from '@mui/joy/TabList'
import Tab from '@mui/joy/Tab'
import TabPanel from '@mui/joy/TabPanel'
import Alert from '@mui/joy/Alert'
import Chip from '@mui/joy/Chip'
import Divider from '@mui/joy/Divider'
import IconButton from '@mui/joy/IconButton'
import Button from '@mui/joy/Button'
import CloseIcon from '@mui/icons-material/Close'
import PeopleIcon from '@mui/icons-material/People'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ChatIcon from '@mui/icons-material/Chat'
import BugReportIcon from '@mui/icons-material/BugReport'
import StorageIcon from '@mui/icons-material/Storage'
import TerminalIcon from '@mui/icons-material/Terminal'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import BadgeIcon from '@mui/icons-material/Badge'
import { useLocation } from 'react-router-dom'

import { CrmCompany, resolveCompanyReview } from 'api/oracleApi'
import { useCompanies } from './hooks/useCompanies'
import { ProcessJobProvider, useProcessJobContext } from './hooks/ProcessJobContext'
import { CompaniesTable } from './components/CompaniesTable'
import { MergeModal } from './components/MergeModal'
import { CompanyFactsList } from './components/CompanyFactsList'
import { CompanyContactsList } from './components/CompanyContactsList'
import { AskPanel } from './components/AskPanel'
import { ProcessJobCard } from './components/ProcessJobCard'
import { DeveloperPanel } from './components/DeveloperPanel'
import MeetingRecorderConsole from './datasource/ds-gmeet/components/MeetingRecorderConsole'
import { FileSearchPanel } from './components/FileSearchPanel'
import { CompanyFileSearchDocs } from './components/CompanyFileSearchDocs'
import { PipelineConstructorTab } from './components/PipelineConstructorTab'
import { TeamMembersTab } from './components/TeamMembersTab'
import { McpAccessPanel } from './components/McpAccessPanel'
import { useTeamMembers } from './hooks/useTeamMembers'
import { useUrlNumberParam } from './hooks/useUrlPathSegment'

type SubPage = 'setup' | 'pipeline' | 'agent' | 'mcp'

function useSubPage(): SubPage {
    const { pathname } = useLocation()
    if (pathname.includes('/crm/setup')) return 'setup'
    if (pathname.includes('/crm/agent')) return 'agent'
    if (pathname.includes('/crm/mcp')) return 'mcp'
    return 'pipeline'
}

export default function OraclePage() {
    const allCompanies = useCompanies(false, true)
    const reviewCompanies = useCompanies(true, true)
    const onJobComplete = async () => {
        await allCompanies.refetch()
        await reviewCompanies.refetch()
    }
    return (
        <ProcessJobProvider onComplete={onJobComplete}>
            <OraclePageInner allCompanies={allCompanies} reviewCompanies={reviewCompanies} />
        </ProcessJobProvider>
    )
}

type CompaniesHook = ReturnType<typeof useCompanies>

function OraclePageInner({ allCompanies, reviewCompanies }: { allCompanies: CompaniesHook; reviewCompanies: CompaniesHook }) {
    const subPage = useSubPage()
    const teamMembers = useTeamMembers()
    const [pipelineTab, setPipelineTab] = useUrlNumberParam('tab', 0)
    const [selected, setSelected] = useState<CrmCompany | null>(null)
    const [mergeSource, setMergeSource] = useState<CrmCompany | null>(null)

    const { job, isCancelling, cancelJob, dismissJob } = useProcessJobContext()
    const showPageJobCard = !!job && !job.source_filter

    const handleSelect = (c: CrmCompany) => setSelected(selected?.id === c.id ? null : c)

    const handleMerge = async (fromId: string, intoId: string) => {
        await allCompanies.merge(fromId, intoId)
        await reviewCompanies.refetch()
        if (selected?.id === fromId) setSelected(null)
    }

    const handleResolve = async () => {
        if (!selected) return
        try {
            await resolveCompanyReview(selected.id)
            await reviewCompanies.refetch()
            setSelected(null)
        } catch (err) {
            console.error('Failed to resolve review:', err)
        }
    }

    const handleDelete = async () => {
        if (!selected) return
        if (!window.confirm(`Delete "${selected.name_display}" and all its data? This cannot be undone.`)) return
        await reviewCompanies.remove(selected.id)
        setSelected(null)
    }

    return (
        <Stack gap={2} sx={{ height: '100%' }}>
            {(allCompanies.error || reviewCompanies.error) && <Alert color="danger">{allCompanies.error || reviewCompanies.error}</Alert>}
            {showPageJobCard && job && <ProcessJobCard job={job} isCancelling={isCancelling} onCancel={cancelJob} onDismiss={dismissJob} />}

            {/* ── Sub-page: Setup ── Pure DataSource configuration. */}
            {subPage === 'setup' && (
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <MeetingRecorderConsole />
                </Box>
            )}

            {/* ── Sub-page: Agent ── */}
            {subPage === 'agent' && (
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <AskPanel />
                </Box>
            )}

            {/* ── Sub-page: MCP Access ── */}
            {subPage === 'mcp' && (
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <McpAccessPanel />
                </Box>
            )}

            {/* ── Sub-page: Pipeline ── */}
            {subPage === 'pipeline' && (
                <Tabs
                    value={pipelineTab}
                    onChange={(_, v) => {
                        setPipelineTab(v as number)
                        setSelected(null)
                    }}
                    sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}
                >
                    <TabList>
                        <Tab>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <AccountTreeIcon fontSize="small" />
                                Constructor
                            </Stack>
                        </Tab>
                        <Tab>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <PeopleIcon fontSize="small" />
                                Companies
                                <Chip size="sm" variant="soft" sx={{ ml: 0.5 }}>
                                    {allCompanies.companies.length}
                                </Chip>
                            </Stack>
                        </Tab>
                        <Tab color={reviewCompanies.companies.length > 0 ? 'warning' : 'neutral'}>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <WarningAmberIcon fontSize="small" />
                                Needs Review
                                {reviewCompanies.companies.length > 0 && (
                                    <Chip size="sm" color="warning" variant="soft" sx={{ ml: 0.5 }}>
                                        {reviewCompanies.companies.length}
                                    </Chip>
                                )}
                            </Stack>
                        </Tab>
                        <Tab>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <StorageIcon fontSize="small" />
                                File Search
                            </Stack>
                        </Tab>
                        <Tab>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <TerminalIcon fontSize="small" />
                                Operations
                            </Stack>
                        </Tab>
                        <Tab>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <BadgeIcon fontSize="small" />
                                Team
                                {teamMembers.members.length > 0 && (
                                    <Chip size="sm" variant="soft" sx={{ ml: 0.5 }}>
                                        {teamMembers.members.length}
                                    </Chip>
                                )}
                            </Stack>
                        </Tab>
                    </TabList>

                    <TabPanel value={0} sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <PipelineConstructorTab />
                    </TabPanel>

                    <TabPanel value={1} sx={{ p: 0, pt: 2, flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: 2, height: '100%', overflow: 'hidden' }}>
                            <Box sx={{ overflow: 'hidden', height: '100%' }}>
                                <CompaniesTable
                                    companies={allCompanies.companies}
                                    loading={allCompanies.loading}
                                    selected={selected}
                                    onSelect={handleSelect}
                                    onMerge={(c) => setMergeSource(c)}
                                />
                            </Box>
                            {selected && (
                                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, overflowY: 'auto' }}>
                                    <DetailPane company={selected} onClose={() => setSelected(null)} />
                                </Sheet>
                            )}
                        </Box>
                    </TabPanel>

                    <TabPanel value={2} sx={{ p: 0, pt: 2, flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: 2, height: '100%', overflow: 'hidden' }}>
                            <Box sx={{ overflow: 'hidden', height: '100%' }}>
                                <CompaniesTable
                                    companies={reviewCompanies.companies}
                                    loading={reviewCompanies.loading}
                                    selected={selected}
                                    onSelect={handleSelect}
                                    onMerge={(c) => setMergeSource(c)}
                                    reviewMode
                                />
                            </Box>
                            {selected && (
                                <Sheet
                                    variant="outlined"
                                    sx={{ borderRadius: 'sm', display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
                                >
                                    <Stack sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                                        <DetailHeader company={selected} onClose={() => setSelected(null)} />
                                    </Stack>
                                    <Stack sx={{ flex: 1, overflowY: 'auto', p: 2, gap: 2, minHeight: 0 }}>
                                        <CompanyFactsList companyId={selected.id} />
                                        <Divider />
                                        <CompanyContactsList companyId={selected.id} />
                                    </Stack>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        gap={1}
                                        sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                                    >
                                        <Button variant="outlined" color="danger" size="sm" onClick={handleDelete}>
                                            Delete
                                        </Button>
                                        <Stack direction="row" gap={1}>
                                            <Button variant="outlined" color="warning" onClick={() => setMergeSource(selected)}>
                                                Merge
                                            </Button>
                                            <Button variant="solid" color="success" onClick={handleResolve}>
                                                Confirm as new
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Sheet>
                            )}
                        </Box>
                    </TabPanel>

                    <TabPanel value={3} sx={{ p: 0, pt: 2, flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        <Box sx={{ height: '100%', overflowY: 'auto' }}>
                            <FileSearchPanel />
                        </Box>
                    </TabPanel>

                    <TabPanel value={4} sx={{ p: 0, pt: 2, flex: 1, overflow: 'hidden', minHeight: 0 }}>
                        <Box sx={{ height: '100%', overflowY: 'auto', pr: 1 }}>
                            <DeveloperPanel />
                        </Box>
                    </TabPanel>

                    <TabPanel value={5} sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 0 }}>
                        <TeamMembersTab />
                    </TabPanel>
                </Tabs>
            )}

            <MergeModal
                open={mergeSource !== null}
                source={mergeSource}
                candidates={allCompanies.companies}
                onMerge={handleMerge}
                onClose={() => setMergeSource(null)}
            />
        </Stack>
    )
}

function DetailHeader({ company, onClose }: { company: CrmCompany; onClose: () => void }) {
    return (
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Stack gap={0.5}>
                <Typography level="title-md">{company.name_display}</Typography>
                <Typography level="body-xs" color="neutral">
                    {company.id}
                </Typography>
            </Stack>
            <IconButton size="sm" variant="plain" onClick={onClose}>
                <CloseIcon />
            </IconButton>
        </Stack>
    )
}

function DetailPane({ company, onClose }: { company: CrmCompany; onClose: () => void }) {
    return (
        <Stack gap={2}>
            <DetailHeader company={company} onClose={onClose} />
            <Divider />
            {company.summary && (
                <>
                    <Stack gap={0.5}>
                        <Typography level="title-sm">Summary</Typography>
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                            {company.summary}
                        </Typography>
                    </Stack>
                    <Divider />
                </>
            )}
            <CompanyFactsList companyId={company.id} />
            <Divider />
            <CompanyContactsList companyId={company.id} />
            <Divider />
            <CompanyFileSearchDocs companyId={company.id} />
        </Stack>
    )
}
